import React, { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';
import {
  Button,
  Checkbox,
  DatePicker,
  Input,
  Modal,
  Select,
  Typography,
} from 'antd';
import moment, { Moment } from 'moment';
// Types
import {
  CampaignAppearanceType,
  CampaignTaskType,
  CreateCampaignTaskV2Input,
} from 'api/graphql-global-types';
import { GetCampaignsV2_getCampaigns_entities_activities_CustomBriefActivity_tasksV2_CampaignAppearanceTask } from 'api/campaignV2/types/GetCampaignsV2';
// Utils
import { APPEARANCE_TYPE_OPTIONS } from 'helpers/customCampaignConstants';
// Styles
import styles from './AppearanceDeliverableModal.module.scss';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

type AppearanceTask =
  GetCampaignsV2_getCampaigns_entities_activities_CustomBriefActivity_tasksV2_CampaignAppearanceTask;

type AppearanceDeliverableFormData = {
  title: string;
  description: string | null;
  appearanceType: CampaignAppearanceType | null;
  appearanceDate: Moment | null;
  durationText: string | null;
  location: string | null;
  travelIncluded: boolean;
  accommodationIncluded: boolean;
  maxExpenses: number | null;
};

type AppearanceDeliverableModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateCampaignTaskV2Input) => void;
  editingItem?: AppearanceTask | null;
};

const defaultValues: AppearanceDeliverableFormData = {
  title: '',
  description: null,
  appearanceType: null,
  appearanceDate: null,
  durationText: null,
  location: null,
  travelIncluded: false,
  accommodationIncluded: false,
  maxExpenses: null,
};

const validationSchema = yup.object().shape({
  title: yup.string().required('Deliverable name is required'),
  appearanceDate: yup.mixed().required('Date & Time is required'),
});

const AppearanceDeliverableModal: React.FC<AppearanceDeliverableModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  editingItem,
}) => {
  const wasOpenRef = useRef(false);
  const { control, errors, handleSubmit, reset, watch } =
    useForm<AppearanceDeliverableFormData>({
      defaultValues,
      resolver: yupResolver(validationSchema),
      mode: 'onChange',
      reValidateMode: 'onChange',
      shouldUnregister: false,
    });

  const travelIncluded = watch('travelIncluded');
  const accommodationIncluded = watch('accommodationIncluded');
  const showMaxExpenses = Boolean(travelIncluded || accommodationIncluded);

  const mapEditingItemToFormData = (): AppearanceDeliverableFormData => {
    const item = editingItem as any;
    const appearance = item?.appearance ?? item ?? {};
    const fixedDate = appearance.appearanceDate ?? item?.dueDate?.fixedDate;

    return {
      title: item?.title ?? '',
      description: item?.description ?? null,
      appearanceType: appearance.appearanceType ?? null,
      appearanceDate: fixedDate ? moment(fixedDate) : null,
      durationText: appearance.durationText ?? null,
      location: appearance.location ?? null,
      travelIncluded: Boolean(appearance.travelIncluded),
      accommodationIncluded: Boolean(appearance.accommodationIncluded),
      maxExpenses: appearance.maxExpenses ?? null,
    };
  };

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      reset(mapEditingItemToFormData());
    }
    wasOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editingItem]);

  const handleValidSubmit = (values: AppearanceDeliverableFormData) => {
    onSubmit({
      type: CampaignTaskType.APPEARANCE,
      title: values.title,
      description: values.description || null,
      appearance: {
        appearanceType: values.appearanceType,
        appearanceDate: values.appearanceDate
          ? values.appearanceDate.toISOString()
          : null,
        durationText: values.durationText || null,
        location: values.location || null,
        travelIncluded: Boolean(values.travelIncluded),
        accommodationIncluded: Boolean(values.accommodationIncluded),
        maxExpenses: values.maxExpenses ?? null,
      },
    });
    reset(defaultValues);
  };

  return (
    <Modal
      title={`${editingItem ? 'Edit' : 'New'} Appearance Deliverable`}
      visible={isOpen}
      onCancel={onClose}
      width={680}
      footer={null}
    >
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.stopPropagation();
          handleSubmit(handleValidSubmit)(event);
        }}
      >
        <div className={styles.formItem}>
          <Text>Deliverable Name</Text>
          <Controller
            name="title"
            control={control}
            render={({ onChange, value }) => (
              <Input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="eg. Adidas Store LA Photoshoot"
                maxLength={100}
              />
            )}
          />
          {errors.title?.message && (
            <Text type="danger">{errors.title.message}</Text>
          )}
        </div>

        <div className={styles.row}>
          <div className={styles.formItem}>
            <Text>Duration</Text>
            <Controller
              name="durationText"
              control={control}
              render={({ onChange, value }) => (
                <Input
                  value={value ?? ''}
                  onChange={(e) => onChange(e.target.value || null)}
                  placeholder="e.g. 3 hours"
                />
              )}
            />
          </div>

          <div className={styles.formItem}>
            <Text>Appearance Type</Text>
            <Controller
              name="appearanceType"
              control={control}
              render={({ onChange, value }) => (
                <Select
                  value={value ?? undefined}
                  onChange={(nextValue) => onChange(nextValue ?? null)}
                  placeholder="Select appearance type"
                  allowClear
                >
                  {APPEARANCE_TYPE_OPTIONS.map((opt) => (
                    <Option value={opt.value} key={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              )}
            />
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.formItem}>
            <Text>Location</Text>
            <Controller
              name="location"
              control={control}
              render={({ onChange, value }) => (
                <Input
                  value={value ?? ''}
                  onChange={(e) => onChange(e.target.value || null)}
                  placeholder="Address or venue"
                />
              )}
            />
          </div>

          <div className={styles.formItem}>
            <Text>Date &amp; Time</Text>
            <Controller
              name="appearanceDate"
              control={control}
              render={({ onChange, value }) => (
                <DatePicker
                  value={value}
                  onChange={onChange}
                  showTime
                  format="MM/DD/YYYY h:mm a"
                  style={{ width: '100%' }}
                  placeholder="MM/DD/YYYY 00:00 AM/PM"
                />
              )}
            />
            {errors.appearanceDate?.message && (
              <Text type="danger">{errors.appearanceDate.message}</Text>
            )}
          </div>
        </div>

        <div className={styles.checkboxRow}>
          <Controller
            name="travelIncluded"
            control={control}
            render={({ onChange, value }) => (
              <Checkbox
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
              >
                Travel expenses included
              </Checkbox>
            )}
          />
          <Controller
            name="accommodationIncluded"
            control={control}
            render={({ onChange, value }) => (
              <Checkbox
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
              >
                Accommodation included
              </Checkbox>
            )}
          />
        </div>

        {showMaxExpenses && (
          <div className={styles.formItem}>
            <Text>Max Expense Cap (Optional)</Text>
            <Controller
              name="maxExpenses"
              control={control}
              render={({ onChange, value }) => (
                <Input
                  value={value ?? ''}
                  onChange={(e) =>
                    onChange(
                      e.target.value === '' ? null : Number(e.target.value)
                    )
                  }
                  type="number"
                  min={0}
                  prefix="$"
                  placeholder="500.00 USD"
                />
              )}
            />
          </div>
        )}

        <div className={styles.formItem}>
          <Text>Description</Text>
          <Controller
            name="description"
            control={control}
            render={({ onChange, value }) => (
              <TextArea
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value || null)}
                rows={4}
                placeholder="Describe what the talent needs to do for this appearance."
              />
            )}
          />
        </div>

        <div className={styles.actions}>
          <Button onClick={onClose}>Cancel</Button>
          <Button htmlType="submit" type="primary">
            {editingItem ? 'Edit Deliverable' : 'Add Deliverable'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AppearanceDeliverableModal;
